import { ApiRequestError, blobRequest, multipartRequest, requestJson, requestVoid } from '../http'

import type {
  ApplicationDetail,
  ApplicationKalpResponse,
  ApplicationListItem,
  CreateApplicationResponse,
  CreateApplicationPayload,
  DistributorDocument,
  DistributorApplicationDetails,
  UpdateApplicationPayload,
  CancelReason,
} from './application.types'
import { mapApplicationDetailsResponseToApplicationDetail } from './application-details.mapper'
import type {
  DistributorApplicationListQuery,
  DistributorApplicationListResponse,
} from './distributor-application-list.types'
import type { WorkResponsibilityParty } from '@staff/shared'
import type {
  SubmitApplicationAdditionPayload,
  SubmittableApplicationAdditionType,
  SubmitApplicationAdditionResponse,
  UploadApplicationDocumentPayload,
  UploadApplicationDocumentResponse,
} from './application-additions.types'

// Re-exported so existing `@/services/application` consumers keep working; the
// canonical home is `services/http.ts`, shared with the commitment service.
export { ApiRequestError }

const createDistributorApplicationListQuery = (query: DistributorApplicationListQuery) => {
  const params = new URLSearchParams()

  if (query.page != null) params.set('page', String(query.page))
  if (query.hitsPerPage != null) params.set('hitsPerPage', String(query.hitsPerPage))
  if (query.status) params.set('status', query.status)
  if (query.type) params.set('type', query.type)
  if (query.hasUnreadMessages) params.set('hasUnreadMessages', 'true')
  if (query.hasOpenConversations) params.set('hasOpenConversations', 'true')
  if (query.hasOpenRequiredAdditions) params.set('hasOpenRequiredAdditions', 'true')
  if (query.isHighPrio) params.set('isHighPrio', 'true')
  if (query.workResponsibilityParty) params.set('workResponsibilityParty', query.workResponsibilityParty)
  if (query.brokerPersonnelUserId) params.set('brokerPersonnelUserId', query.brokerPersonnelUserId)
  if (query.orderBy) params.set('orderBy', query.orderBy)
  if (query.orderDirection) params.set('orderDirection', query.orderDirection)

  const queryString = params.toString()
  return queryString ? `?${queryString}` : ''
}

/**
 * Distributor application listing.
 * Uses the new paginated /borgo/distributor/applications contract and must not be mixed with detail response types.
 */
export const fetchDistributorApplications = async (
  query: DistributorApplicationListQuery = {},
): Promise<DistributorApplicationListResponse> => {
  const queryString = createDistributorApplicationListQuery(query)
  return await requestJson<DistributorApplicationListResponse>(`/borgo/distributor/applications${queryString}`, 'GET')
}

export const fetchDraftApplications = async (): Promise<ApplicationListItem[]> => {
  const response = await fetchDistributorApplications({ status: 'draft', hitsPerPage: 100 })

  return response.hits.map((application) => ({
    id: application.id,
    status: application.status,
    type: application.type,
    amountAppliedFor: application.amountAppliedFor ?? undefined,
    brokerExternalId: application.brokerExternalId,
    brokerPersonnelUserId: application.brokerPersonnelUserId,
    createdAt: application.createdAt,
    updatedAt: application.updatedAt,
    submittedAt: application.submittedAt,
    applicants: [
      {
        id: application.mainApplicant.userId ?? undefined,
        name: [application.mainApplicant.givenName, application.mainApplicant.surname].filter(Boolean).join(' '),
        isMainApplicant: true,
      },
    ],
  }))
}

export const fetchApplicationById = async (id: string): Promise<DistributorApplicationDetails> => {
  return await requestJson<DistributorApplicationDetails>(`/borgo/distributor/applications/${id}`, 'GET')
}

export const fetchApplicationKalp = async (id: string): Promise<ApplicationKalpResponse> => {
  return await requestJson<ApplicationKalpResponse>(`/borgo/distributor/applications/${id}/kalp`, 'GET')
}

export const setDistributorWorkResponsibility = async (
  id: string,
  responsibility: WorkResponsibilityParty,
): Promise<void> => {
  await requestVoid(`/borgo/distributor/applications/${id}/work-responsibility`, 'POST', { responsibility })
}

export const setBrokerPersonnel = async (id: string, brokerPersonnelUserId: string): Promise<void> => {
  await requestVoid(`/borgo/distributor/applications/${id}/broker-personnel`, 'POST', { brokerPersonnelUserId })
}

export const fetchApplicationFormById = async (id: string): Promise<ApplicationDetail> => {
  const application = await requestJson<DistributorApplicationDetails>(`/borgo/distributor/applications/${id}`, 'GET')
  return mapApplicationDetailsResponseToApplicationDetail(application)
}

export const createApplication = async (payload: CreateApplicationPayload): Promise<CreateApplicationResponse> => {
  return await requestJson<CreateApplicationResponse>('/borgo/distributor/applications/create', 'POST', payload)
}

export const updateApplication = async (id: string, payload: UpdateApplicationPayload): Promise<void> => {
  await requestVoid(`/borgo/distributor/applications/${id}`, 'POST', payload)
}

export const submitApplication = async (id: string): Promise<{ message: string }> => {
  return await requestJson<{ message: string }>(`/borgo/distributor/applications/${id}/submit`, 'POST')
}

export const submitApplicationAddition = async <TType extends SubmittableApplicationAdditionType>(
  id: string,
  payload: SubmitApplicationAdditionPayload<TType>,
): Promise<SubmitApplicationAdditionResponse> => {
  return await requestJson<SubmitApplicationAdditionResponse>(
    `/borgo/distributor/applications/${id}/application-additions`,
    'POST',
    payload,
  )
}

export const uploadApplicationDocument = async (
  id: string,
  payload: UploadApplicationDocumentPayload,
): Promise<UploadApplicationDocumentResponse> => {
  const formData = new FormData()
  formData.append('file', payload.file)
  formData.append('type', payload.type)

  return await multipartRequest<UploadApplicationDocumentResponse>(
    `/borgo/distributor/applications/${id}/documents`,
    'POST',
    formData,
  )
}

export const fetchDocuments = async (filters: { applicationId?: string; userId?: string; debtId?: string }) => {
  const query = new URLSearchParams()

  if (filters.applicationId) {
    query.set('applicationId', filters.applicationId)
  }
  if (filters.userId) {
    query.set('userId', filters.userId)
  }
  if (filters.debtId) {
    query.set('debtId', filters.debtId)
  }

  const queryString = query.toString()

  return await requestJson<DistributorDocument[]>(
    `/borgo/distributor/documents${queryString ? `?${queryString}` : ''}`,
    'GET',
  )
}

export const downloadDocument = async (documentId: string) => {
  return await blobRequest(`/borgo/distributor/documents/${documentId}/download`)
}

export const cancelApplication = async (id: string, cancelReasons: { code: CancelReason }[]): Promise<void> => {
  await requestVoid(`/borgo/distributor/applications/${id}/cancel`, 'POST', { cancelReasons })
}

export const createLoanAgreements = async (id: string): Promise<void> => {
  await requestVoid(`/borgo/distributor/applications/${id}/loan-agreements`, 'POST')
}

export const deleteDraftApplication = async (id: string): Promise<void> => {
  await requestVoid(`/borgo/distributor/applications/${id}`, 'DELETE')
}
